import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import AgentLayout from './AgentLayout';
import AgentPostJob from './AgentPostJob';
import FixLocationsModal from './FixLocationsModal';
import { suggestCity, hasWrongLocation } from './locationFix';
import ActionMenu from './ActionMenu';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');


const waLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
};

const CALL_BTN = 'inline-flex items-center justify-center gap-1 text-center text-xs font-semibold px-3 py-1.5 rounded-full border transition';

const FILTER_INPUT_CLS =
  'w-full border border-purple-200 rounded-full px-3 py-1.5 text-xs text-gray-700 placeholder:text-gray-400 bg-white ' +
  'hover:border-purple-400 transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const FILTER_LABEL_CLS = 'block text-[10px] font-semibold uppercase tracking-wide text-purple-600 mb-1 ml-1';
const FILTER_SELECT_CLS =
  'w-full appearance-none cursor-pointer border border-purple-200 rounded-full pl-3 pr-8 py-1.5 text-xs font-medium text-purple-700 ' +
  'bg-gradient-to-r from-purple-50 to-pink-50 hover:border-purple-400 transition ' +
  'focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const inDateRange = (value, from, to) => {
  if (!from && !to) return true;
  if (!value) return false;
  const d = new Date(value).setHours(0, 0, 0, 0);
  if (from && d < new Date(from).setHours(0, 0, 0, 0)) return false;
  if (to && d > new Date(to).setHours(23, 59, 59, 999)) return false;
  return true;
};
const isAgentAdded = (e) => e.created_by_agent_id != null || e.agent_id != null;


const isVerified = (e) => e.company_verified_status === 'verified';

const isMergedAway = (e) => e.merged_into_id != null;
const hasMerged = (e) => Number(e.merged_count) > 0;

const SORT_GETTERS = {
  name: (e) => (e.name || '').toLowerCase(),
  email: (e) => (e.email || '').toLowerCase(),
  phone: (e) => String(e.phone || ''),
  location: (e) => (e.location || e.city || '').toLowerCase(),
  jobs: (e) => Number(e.jobs_count || 0),
  created: (e) => (e.created_at ? new Date(e.created_at).getTime() : 0),
  lastContact: (e) => (e.my_last_contact ? new Date(e.my_last_contact).getTime() : 0),
  assigned: (e) => (e.is_mine ? 'you' : (e.assigned_agent_name || '~').toLowerCase()),
};

function SortTh({ label, k, sort, onSort }) {
  const active = sort.key === k;
  return (
    <th className="px-5 py-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 uppercase hover:text-purple-600 transition ${active ? 'text-purple-600' : ''}`}
      >
        {label}
        <span className="text-[9px]">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
}

// User types "example.com" (no protocol) — this adds "https://" before saving.
// Left untouched if the user already typed http:// or https://.
const normalizeWebsite = (value) => {
  const v = (value || '').trim();
  if (!v) return v;
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

// Debounce delay (ms) before firing an AI suggestion request while typing.
const SUGGEST_DEBOUNCE_MS = 350;

// Free-text input with a suggestion dropdown — used for the Company and Location filters.
function FilterSearchInput({ value, onChange, suggestions, placeholder }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const base = value
      ? suggestions.filter((s) => s.toLowerCase().includes(value.toLowerCase()))
      : suggestions;
    return base.slice(0, 8);
  }, [suggestions, value]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D]"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          {filtered.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => { onChange(s); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchableSelect({ value, onChange, options, allLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQ('');
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={FILTER_SELECT_CLS + ' text-left truncate'}
      >
        {value || allLabel}
      </button>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[200px] bg-white border border-purple-100 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="w-full border border-purple-200 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => pick('')}
              className="block w-full text-left px-3 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50"
            >
              {allLabel}
            </button>
            {filtered.map((o) => (
              <button
                type="button"
                key={o}
                onClick={() => pick(o)}
                className={`block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600 ${
                  o === value ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'
                }`}
              >
                {o}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MultiSelect({ value, onChange, options, allLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const toggle = (o) =>
    onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);

  const label =
    value.length === 0 ? allLabel : value.length === 1 ? value[0] : `${value.length} selected`;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={FILTER_SELECT_CLS + ' text-left truncate'}
      >
        {label}
      </button>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[220px] bg-white border border-purple-100 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="w-full border border-purple-200 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {value.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="block w-full text-left px-3 py-2 text-sm font-semibold text-pink-600 hover:bg-purple-50"
              >
                Clear selection
              </button>
            )}
            {filtered.map((o) => (
              <label
                key={o}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(o)}
                  onChange={() => toggle(o)}
                  className="h-4 w-4 rounded border-gray-300 accent-purple-600"
                />
                <span className="truncate">{o}</span>
              </label>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


const JOB_STATUS_STYLES = {
  active: 'bg-green-50 text-green-600',
  closed: 'bg-gray-100 text-gray-500',
  paused: 'bg-amber-50 text-amber-600',
  draft: 'bg-purple-50 text-purple-600',
};

const JOB_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'title_asc', label: 'Title A–Z' },
  { value: 'title_desc', label: 'Title Z–A' },
  { value: 'applicants_desc', label: 'Most applicants' },
  { value: 'salary_desc', label: 'Highest salary' },
  { value: 'status', label: 'Status' },
];

function EmployerJobsModal({ employer, onClose }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sortBy, setSortBy] = useState('newest');


  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/agent/jobs', { params: { employer_id: employer.id } });
        setJobs(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to load employer jobs', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [employer.id]);

  const sortedJobs = useMemo(() => {
    const list = jobs.slice();
    const time = (j) => new Date(j.created_at || 0).getTime();
    const apps = (j) => Number(j.applications_count ?? j.job_applications_count ?? 0);
    const sal = (j) => Number(j.salary_max || j.salary_min || 0);
    switch (sortBy) {
      case 'oldest': return list.sort((a, b) => time(a) - time(b));
      case 'title_asc': return list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      case 'title_desc': return list.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
      case 'applicants_desc': return list.sort((a, b) => apps(b) - apps(a));
      case 'salary_desc': return list.sort((a, b) => sal(b) - sal(a));
      case 'status': return list.sort((a, b) => (a.status || '').localeCompare(b.status || ''));
      default: return list.sort((a, b) => time(b) - time(a));
    }
  }, [jobs, sortBy]);

  const money = (n) => (n ? '₹' + Number(n).toLocaleString('en-IN') : '—');

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-white truncate">{employer.name}</h3>
            <p className="text-purple-100 text-xs mt-0.5">
              {loading ? 'Loading jobs…' : `${jobs.length} job${jobs.length === 1 ? '' : 's'} posted`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-purple-100 hover:text-white hover:bg-white/10 rounded-full h-8 w-8 flex items-center justify-center text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3 shrink-0">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sort by</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            {JOB_SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="overflow-auto">
          {loading ? (
            <p className="text-center text-gray-400 py-10 text-sm">Loading…</p>
          ) : error ? (
            <p className="text-center text-red-500 py-10 text-sm">Could not load jobs.</p>
          ) : sortedJobs.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">No jobs posted.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-[10px] uppercase bg-gray-50/60 border-b border-gray-100">
                  <th className="px-5 py-3 font-medium">Title</th>
                  <th className="px-5 py-3 font-medium">Location</th>
                  <th className="px-5 py-3 font-medium">Salary</th>
                  <th className="px-5 py-3 font-medium">Applicants</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Posted</th>
                </tr>
              </thead>
              <tbody>
                {sortedJobs.map((j) => (
                  <tr key={j.id} className="border-t border-gray-50 hover:bg-purple-50/30">
                    <td className="px-5 py-3 font-medium text-gray-800">{j.title}</td>
                    <td className="px-5 py-3">{j.location || '—'}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{money(j.salary_min)} – {money(j.salary_max)}</td>
                    <td className="px-5 py-3 text-center">{j.applications_count ?? j.job_applications_count ?? 0}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${JOB_STATUS_STYLES[j.status] || 'bg-gray-50 text-gray-500'}`}>
                        {j.status || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">{fmtDate(j.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function MergeEmployersModal({ employers, onClose, onConfirm, loading }) {
  const [primaryId, setPrimaryId] = useState(employers[0].id);
  const primary = employers.find((e) => e.id === primaryId);
  const secondary = employers.find((e) => e.id !== primaryId);

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
      onClick={loading ? undefined : onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-bold text-white">Merge Employer Accounts</h3>
            <p className="text-purple-100 text-xs mt-0.5">Select the account you want to keep as the main account</p>
          </div>
          <button onClick={onClose} disabled={loading} className="text-purple-100 hover:text-white text-2xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {employers.map((e) => {
              const isMain = e.id === primaryId;
              return (
                <label
                  key={e.id}
                  className={`cursor-pointer rounded-2xl border-2 p-4 transition ${
                    isMain ? 'border-purple-500 bg-purple-50/50' : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                      isMain ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {isMain ? 'Main account' : 'Will be merged'}
                    </span>
                    <input
                      type="radio"
                      name="primary"
                      checked={isMain}
                      onChange={() => setPrimaryId(e.id)}
                      className="accent-purple-600 h-4 w-4"
                    />
                  </div>
                  <p className="font-semibold text-gray-800 truncate">{e.name}</p>
                  <dl className="mt-2 text-xs text-gray-600 space-y-1">
                    <div><span className="text-gray-400">Email: </span>{e.email || '—'}</div>
                    <div><span className="text-gray-400">Phone: </span>{e.phone || '—'}</div>
                    <div><span className="text-gray-400">Location: </span>{e.location || e.city || '—'}</div>
                    <div><span className="text-gray-400">Jobs: </span>{e.jobs_count ?? 0}</div>
                    <div><span className="text-gray-400">Created: </span>{fmtDate(e.created_at)}</div>
                  </dl>
                </label>
              );
            })}
          </div>

          <div className="mt-5 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-xs text-amber-800 space-y-1">
            <p className="font-semibold">After merge:</p>
            <p>• <b>{primary.name}</b> stays active with email <b>{primary.email || '—'}</b> and phone <b>{primary.phone || '—'}</b>.</p>
            <p>• Alternate email: <b>{secondary.email || '—'}</b> · Alternate phone: <b>{secondary.phone || '—'}</b></p>
            <p>• All jobs of <b>{secondary.name}</b> move to the main account, and that account is disabled.</p>
            <p>• Only an admin can undo this.</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
          <button onClick={onClose} disabled={loading} className="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(primary.id, secondary.id)}
            disabled={loading}
            className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white px-6 py-2 rounded-full text-sm font-semibold disabled:opacity-50 shadow-sm"
          >
            {loading ? 'Merging…' : 'Confirm merge'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AgentEmployers() {
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
 const [companyFilter, setCompanyFilter] = useState([]);
const [locationFilter, setLocationFilter] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewScope, setViewScope] = useState('all'); // 'mine' | 'all'
  const [assigningId, setAssigningId] = useState(null);
  const [viewEmployer, setViewEmployer] = useState(null);
  const [jobsEmployer, setJobsEmployer] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [noEmailOnly, setNoEmailOnly] = useState(false);
  const [stateFilter, setStateFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('');
const [selectedIds, setSelectedIds] = useState(new Set());
const [bulkLoading, setBulkLoading] = useState(false);
const [mergeOpen, setMergeOpen] = useState(false);
const [mergeLoading, setMergeLoading] = useState(false);
const [sort, setSort] = useState({ key: '', dir: '' });
const [showFilters, setShowFilters] = useState(false);
const [fixOpen, setFixOpen] = useState(false);
const [wrongLocOnly, setWrongLocOnly] = useState(false);
const [verifiedFilter, setVerifiedFilter] = useState('');
const [mergeFilter, setMergeFilter] = useState('');

// Only employers this agent can actually edit (same rule as isSelectable)
const fixableEmployers = useMemo(
  () => employers.filter((e) => viewScope === 'mine' || e.is_mine),
  [employers, viewScope]
);
const wrongLocCount = useMemo(
  () => fixableEmployers.filter((e) => hasWrongLocation(e.location || e.city)).length,
  [fixableEmployers]
);

const [noLocOnly, setNoLocOnly] = useState(false);
const noLocCount = useMemo(
  () => employers.filter((e) => !(e.location || e.city) || !String(e.location || e.city).trim()).length,
  [employers]
);

useEffect(() => {
  if (wrongLocCount === 0 && wrongLocOnly) setWrongLocOnly(false);
}, [wrongLocCount, wrongLocOnly]);


const [page, setPage] = useState(1);
const [pageSize, setPageSize] = useState(25);

useEffect(() => {
  setPage(1);
}, [search, companyFilter, locationFilter, stateFilter, statusFilter, sourceFilter, verifiedFilter, mergeFilter, dateFrom, dateTo, noEmailOnly, wrongLocOnly, noLocOnly, sort, viewScope, pageSize]);

const handleLocationApplied = (from, to) => {
  const f = String(from).toLowerCase();
  setEmployers((prev) =>
    prev.map((e) =>
      (viewScope === 'mine' || e.is_mine) && (e.city || '').toLowerCase() === f
        ? { ...e, city: to }
        : e
    )
  );
};

  const emptyForm = {
    name: '', email: '', phone: '', password: '',
    location: '', pincode: '',
    company_website: '', company_about: '', company_gst: '',
    company_size: '', company_industry: '',
  };

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  // --- AI autocomplete: Company Name ---
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const companyDebounceRef = useRef(null);

  // --- AI autocomplete: Current Location ---
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const locationDebounceRef = useRef(null);

  // --- AI autocomplete: Company Industry ---
  const [industrySuggestions, setIndustrySuggestions] = useState([]);
  const [showIndustrySuggestions, setShowIndustrySuggestions] = useState(false);
  const [industryLoading, setIndustryLoading] = useState(false);
  const industryDebounceRef = useRef(null);

  const loadEmployers = useCallback(async (scope) => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.get('/agent/employers', { params: { scope: scope || viewScope } });
      setEmployers(res.data);
    } catch (err) {
      console.error('Failed to load employers', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [viewScope]);

  useEffect(() => {
    setSelectedIds(new Set());
    loadEmployers(viewScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewScope]);

  const handleAssign = async (employerId, reassign = false) => {
    setAssigningId(employerId);
    try {
      await api.put(`/agent/employers/${employerId}/assign-to-me`, { reassign });
      loadEmployers(viewScope);
    } catch (err) {
      if (err.response?.status === 422 && !reassign) {
        const ok = window.confirm(
          `${err.response.data?.message || 'This employer is already assigned to another agent.'} Reassign to yourself?`
        );
        if (ok) {
          await handleAssign(employerId, true);
          return;
        }
      } else {
        alert(err.response?.data?.message || 'Failed to assign employer.');
      }
    } finally {
      setAssigningId(null);
    }
  };

  const handleDisableEmployer = async (emp) => {
  if (!window.confirm(`Disable ${emp.name}? Only an admin can re-enable them.`)) return;
  try {
    const res = await api.put(`/agent/employers/${emp.id}/disable`);
    setEmployers((prev) => prev.map((x) => (x.id === emp.id ? { ...x, status: res.data.employer.status } : x)));
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to disable employer.');
  }
};

const isSelectable = (e) =>
  (e.status || 'active') === 'active' && !isMergedAway(e) && (viewScope === 'mine' || e.is_mine);

const toggleSelect = (id) => {
  setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
};

const selectableIds = () => filteredEmployers.filter(isSelectable).map((e) => e.id);


const allSelected = () => {
  const ids = selectableIds();
  return ids.length > 0 && ids.every((id) => selectedIds.has(id));
};

const toggleSelectAll = () => {
  setSelectedIds(allSelected() ? new Set() : new Set(selectableIds()));
};

const handleBulkDisable = async () => {
  if (selectedIds.size === 0) return;
  if (!window.confirm(`Disable ${selectedIds.size} selected employer(s)? Only an admin can re-enable them.`)) return;
  setBulkLoading(true);
  try {
    const res = await api.post('/agent/employers/bulk-disable', { ids: Array.from(selectedIds) });
    const done = new Set(res.data.disabled_ids);
    setEmployers((prev) => prev.map((x) => (done.has(x.id) ? { ...x, status: 'suspended' } : x)));
    setSelectedIds(new Set());
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to disable selected employers.');
  } finally {
    setBulkLoading(false);
  }
};


const selectedEmployers = employers.filter((e) => selectedIds.has(e.id));

const handleMerge = async (primaryId, secondaryId) => {
  setMergeLoading(true);
  try {
    await api.post('/agent/employers/merge', {
      primary_id: primaryId,
      secondary_id: secondaryId,
    });
    setMergeOpen(false);
    setSelectedIds(new Set());
    loadEmployers(viewScope);
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to merge employers.');
  } finally {
    setMergeLoading(false);
  }
};

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Strips any protocol the user pastes in, since the field already shows a
  // fixed "https://" prefix — avoids ending up with "https://https://...".
  const handleWebsiteChange = (e) => {
    const value = e.target.value.replace(/^https?:\/\//i, '');
    setForm((f) => ({ ...f, company_website: value }));
  };

  // ---- Company name AI suggestions ----
  const handleCompanyNameChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, name: value }));

    if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);

    if (value.trim().length < 2) {
      setCompanySuggestions([]);
      setShowCompanySuggestions(false);
      return;
    }

    companyDebounceRef.current = setTimeout(async () => {
      setCompanyLoading(true);
      try {
        const res = await api.post('/ai/suggest-companies', { query: value.trim() });
        setCompanySuggestions(res.data?.suggestions || []);
        setShowCompanySuggestions(true);
      } catch (err) {
        console.error('Company suggestion failed', err);
      } finally {
        setCompanyLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  const pickCompanySuggestion = (value) => {
    setForm((f) => ({ ...f, name: value }));
    setShowCompanySuggestions(false);
    setCompanySuggestions([]);
  };

  // ---- Current location AI suggestions ----
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, location: value }));

    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);

    if (value.trim().length < 2) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    locationDebounceRef.current = setTimeout(async () => {
      setLocationLoading(true);
      try {
        const res = await api.post('/ai/suggest-cities', { query: value.trim() });
        setLocationSuggestions(res.data?.suggestions || []);
        setShowLocationSuggestions(true);
      } catch (err) {
        console.error('Location suggestion failed', err);
      } finally {
        setLocationLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  // ---- Company Industry AI suggestions ----
  const handleIndustryChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, company_industry: value }));

    if (industryDebounceRef.current) clearTimeout(industryDebounceRef.current);

    if (value.trim().length < 2) {
      setIndustrySuggestions([]);
      setShowIndustrySuggestions(false);
      return;
    }

    industryDebounceRef.current = setTimeout(async () => {
      setIndustryLoading(true);
      try {
        const res = await api.post('/ai/suggest-industries', { query: value.trim() });
        setIndustrySuggestions(res.data?.suggestions || []);
        setShowIndustrySuggestions(true);
      } catch (err) {
        console.error('Industry suggestion failed', err);
      } finally {
        setIndustryLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  const pickIndustrySuggestion = (value) => {
    setForm((f) => ({ ...f, company_industry: value }));
    setShowIndustrySuggestions(false);
    setIndustrySuggestions([]);
  };

  const pickLocationSuggestion = (value) => {
    setForm((f) => ({ ...f, location: value }));
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});
    setSuccess(false);
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const closeAddForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});
    setSuccess(false);
    try {
      const payload = {
        ...form,
        company_website: normalizeWebsite(form.company_website),
      };
      await api.post('/agent/employers', payload);
      setSuccess(true);
      loadEmployers();
      // brief success flash, then close the modal
      setTimeout(() => {
        closeAddForm();
      }, 700);
    } catch (err) {
      const data = err.response?.data;
      setErrors(data?.errors || { general: [data?.message || 'Something went wrong'] });
    } finally {
      setSaving(false);
    }
  };

  const knownCompanyNames = useMemo(
    () => Array.from(new Set(employers.map((e) => e.name).filter(Boolean))).sort(),
    [employers]
  );
  const knownLocations = useMemo(
    () => ['Not Specified', ...Array.from(new Set(employers.map((e) => e.location || e.city).filter(Boolean))).sort()],
    [employers]
  );

    const employerState = (e) =>
    (e.state || ((e.location || e.city || '').split(',')[1] || '')).trim();

  const knownStates = useMemo(
    () => Array.from(new Set(employers.map(employerState).filter(Boolean))).sort(),
    [employers]
  );

  const filteredEmployers = employers.filter((e) => {
    const q = search.trim().toLowerCase();
    const matchesSearch = !q || (
      e.name?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) ||
      e.phone?.toLowerCase().includes(q) ||
      (e.location || e.city || '').toLowerCase().includes(q)
    );

    const matchesCompany = companyFilter.length === 0 || companyFilter.includes(e.name);

    const matchesLocation =
      locationFilter.length === 0 ||
      locationFilter.some((l) =>
        l === 'Not Specified' ? !(e.location || e.city) : (e.location || e.city) === l
      );


    

    const matchesDate = inDateRange(e.created_at, dateFrom, dateTo);
    

    const matchesStatus = !statusFilter || (e.status || 'active') === statusFilter;

const matchesNoEmail = !noEmailOnly || !(e.email && String(e.email).trim() !== '');

const matchesState = stateFilter.length === 0 || stateFilter.includes(employerState(e));

const matchesSource =
  !sourceFilter || (sourceFilter === 'agent' ? isAgentAdded(e) : !isAgentAdded(e));

const matchesWrongLoc = !wrongLocOnly || hasWrongLocation(e.location || e.city);
const matchesNoLoc = !noLocOnly || !String(e.location || e.city || '').trim();

const matchesVerified = (() => {
  if (!verifiedFilter) return true;
  const hasJobs = Number(e.jobs_count) > 0;
  switch (verifiedFilter) {
    case 'verified': return isVerified(e);
    case 'verified_posted': return isVerified(e) && hasJobs;
    case 'verified_not_posted': return isVerified(e) && !hasJobs;
    case 'not_verified': return !isVerified(e);
    default: return true;
  }
})();

const matchesMerge = (() => {
  switch (mergeFilter) {
    case 'merged':     return isMergedAway(e);
    case 'has_merged': return hasMerged(e);
    case 'not_merged': return !isMergedAway(e) && !hasMerged(e);
    default:           return true;
  }
})();

return matchesSearch && matchesCompany && matchesLocation && matchesDate && matchesStatus && matchesNoEmail && matchesState && matchesSource && matchesWrongLoc && matchesNoLoc && matchesVerified && matchesMerge;
  });

const cycleSort = (key) =>
  setSort((s) =>
    s.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : { key: '', dir: '' }
  );

  const displayedEmployers = (() => {
  const get = SORT_GETTERS[sort.key];
  if (!get) return filteredEmployers;
  const m = sort.dir === 'asc' ? 1 : -1;
  return [...filteredEmployers].sort((a, b) => {
    const x = get(a), y = get(b);
    return typeof x === 'number' ? (x - y) * m : String(x).localeCompare(String(y)) * m;
  });
})();

const totalPages = Math.max(1, Math.ceil(displayedEmployers.length / pageSize));
const curPage = Math.min(page, totalPages);
const pagedEmployers = displayedEmployers.slice((curPage - 1) * pageSize, curPage * pageSize);

const activeFilterCount = [
  companyFilter.length > 0, locationFilter.length > 0, stateFilter.length > 0,
  !!statusFilter, !!sourceFilter, !!verifiedFilter, !!mergeFilter, !!dateFrom, !!dateTo, noEmailOnly,
].filter(Boolean).length;

const hasActiveFilters = search || companyFilter.length > 0 || locationFilter.length > 0 || dateFrom || dateTo || statusFilter || sourceFilter || verifiedFilter || mergeFilter || noEmailOnly || stateFilter.length > 0 || wrongLocOnly|| noLocOnly;

const clearFilters = () => {
  setSearch('');
  setCompanyFilter([]);
  setLocationFilter([]);
  setDateFrom('');
  setDateTo('');
  setStatusFilter('');
  setNoEmailOnly(false);
  setStateFilter([]);
  setSourceFilter('');
  setVerifiedFilter('');
    setMergeFilter('');
  setWrongLocOnly(false);
  setNoLocOnly(false);
};
  const field = (name, label, type = 'text') => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={handleChange}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D]"
      />
      {errors[name] && <p className="text-red-500 text-xs mt-1">{errors[name][0]}</p>}
    </div>
  );

  // After
return (
  <AgentLayout>
    <div className="-m-4 sm:-m-6 p-4 sm:p-6 min-h-[calc(100vh-57px)] bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9]">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Employers</h2>
        <button
  onClick={openAddForm}
  className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white text-sm font-semibold px-5 py-2.5 rounded-full shadow-sm transition"
>
  + Add Employer
</button>
      </div>
      <p className="text-gray-500 text-sm mb-6">All employers in the system.</p>

           <div className="flex gap-2 mb-4">
        <button
          onClick={() => setViewScope('all')}
          className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${
            viewScope === 'all'
              ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-purple-50'
          }`}
        >
          All Employers
        </button>
        <button
          onClick={() => setViewScope('mine')}
          className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${
            viewScope === 'mine'
              ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-purple-50'
          }`}
        >
          My Employers
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-3 mb-4">
  {/* Top bar: search + filter toggle + clear */}
  <div className="flex flex-col sm:flex-row gap-3">
    <div className="relative flex-1">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm pointer-events-none">🔍</span>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by company, email, phone or location..."
        className={FILTER_INPUT_CLS + ' pl-10 pr-10 py-2'}
      />
      {search && (
        <button
          type="button"
          onClick={() => setSearch('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-gray-400 hover:bg-purple-50 hover:text-purple-600"
          aria-label="Clear search"
        >
          ×
        </button>
      )}
    </div>

    <button
      type="button"
      onClick={() => setShowFilters((s) => !s)}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition ${
        showFilters || activeFilterCount > 0
          ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
          : 'bg-white text-purple-700 border-purple-200 hover:border-purple-400'
      }`}
    >
      ⚙ Filters
      {activeFilterCount > 0 && (
        <span className="bg-white text-purple-600 text-[11px] font-bold h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center">
          {activeFilterCount}
        </span>
      )}
      <span className="text-[10px]">{showFilters ? '▲' : '▼'}</span>
    </button>

{wrongLocCount > 0 && (
  <>
    <button
      type="button"
      onClick={() => { setWrongLocOnly((v) => !v); setNoLocOnly(false); }}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition ${
        wrongLocOnly
          ? 'bg-amber-500 text-white border-transparent shadow-sm'
          : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
      }`}
    >
      ⚠ Wrong location
      <span className="bg-white text-amber-700 text-[11px] font-bold h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center">
        {wrongLocCount}
      </span>
    </button>
    {/* <button
      type="button"
      onClick={() => setFixOpen(true)}
      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-sm hover:opacity-90 transition"
    >
      ✏️ Fix locations
    </button> */}
  </>
)}

{noLocCount > 0 && (
  <button
    type="button"
    onClick={() => { setNoLocOnly((v) => !v); setWrongLocOnly(false); }}
    className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition ${
      noLocOnly
        ? 'bg-rose-500 text-white border-transparent shadow-sm'
        : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
    }`}
  >
    📍 No location
    <span className="bg-white text-rose-700 text-[11px] font-bold h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center">
      {noLocCount}
    </span>
  </button>
)}

    {hasActiveFilters && (
      <button
        onClick={clearFilters}
        className="text-sm font-semibold px-4 py-2 rounded-full bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition"
      >
        ✕ Clear all
      </button>
    )}
  </div>

  {/* Collapsible filter panel */}
  {showFilters && (
   <div className="mt-3 pt-3 border-t border-purple-50">
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className={FILTER_LABEL_CLS}>Company</label>
          <MultiSelect value={companyFilter} onChange={setCompanyFilter} options={knownCompanyNames} allLabel="All Companies" />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Location</label>
          <MultiSelect value={locationFilter} onChange={setLocationFilter} options={knownLocations} allLabel="All Locations" />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>State</label>
          <MultiSelect value={stateFilter} onChange={setStateFilter} options={knownStates} allLabel="All States" />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Status</label>
          <div className="relative">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>

        <div>
  <label className={FILTER_LABEL_CLS}>Source</label>
  <div className="relative">
    <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className={FILTER_SELECT_CLS}>
      <option value="">All</option>
      <option value="agent">Added by agent</option>
      <option value="direct">Direct register</option>
    </select>
    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
  </div>
</div>

<div>
  <label className={FILTER_LABEL_CLS}>Verification</label>
  <div className="relative">
    <select value={verifiedFilter} onChange={(e) => setVerifiedFilter(e.target.value)} className={FILTER_SELECT_CLS}>
      <option value="">All</option>
      <option value="verified">Verified</option>
      <option value="verified_posted">Verified, job posted</option>
      <option value="verified_not_posted">Verified, no job posted</option>
      <option value="not_verified">Not verified</option>
    </select>
    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
  </div>
</div>

<div>
  <label className={FILTER_LABEL_CLS}>Merge status</label>
  <div className="relative">
    <select value={mergeFilter} onChange={(e) => setMergeFilter(e.target.value)} className={FILTER_SELECT_CLS}>
      <option value="">All</option>
      <option value="merged">Merged (second accounts)</option>
      <option value="has_merged">Has merged accounts (main)</option>
      <option value="not_merged">Not merged</option>
    </select>
    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
  </div>
</div>
        <div>
          <label className={FILTER_LABEL_CLS}>Created From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={FILTER_INPUT_CLS} />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Created To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={FILTER_INPUT_CLS} />
        </div>
<div className="flex items-end">
  <label
    className={`flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-1.5 rounded-full border transition w-full ${
              noEmailOnly
                ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
                : 'bg-white text-gray-600 border-purple-200 hover:border-purple-400'
            }`}
          >
            <input
              type="checkbox"
              checked={noEmailOnly}
              onChange={(e) => setNoEmailOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 accent-purple-600"
            />
            No email only
          </label>
        </div>
      </div>
    </div>
  )}

  {/* Active filter chips + count */}
  <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
    <div className="flex flex-wrap gap-2">
      {[
        ...companyFilter.map((v) => ({ k: 'c' + v, label: v, remove: () => setCompanyFilter(companyFilter.filter((x) => x !== v)) })),
        ...locationFilter.map((v) => ({ k: 'l' + v, label: '📍 ' + v, remove: () => setLocationFilter(locationFilter.filter((x) => x !== v)) })),
        ...stateFilter.map((v) => ({ k: 's' + v, label: v, remove: () => setStateFilter(stateFilter.filter((x) => x !== v)) })),
        ...(statusFilter ? [{ k: 'st', label: 'Status: ' + statusFilter, remove: () => setStatusFilter('') }] : []),
         ...(sourceFilter ? [{ k: 'src', label: sourceFilter === 'agent' ? 'Added by agent' : 'Direct register', remove: () => setSourceFilter('') }] : []),
        ...(verifiedFilter ? [{ k: 'vf', label: {
  verified: 'Verified',
  verified_posted: 'Verified, job posted',
  verified_not_posted: 'Verified, no job posted',
  not_verified: 'Not verified',
}[verifiedFilter], remove: () => setVerifiedFilter('') }] : []),

...(mergeFilter ? [{ k: 'mg', label: { merged: 'Merged accounts', has_merged: 'Has merged accounts', not_merged: 'Not merged' }[mergeFilter], remove: () => setMergeFilter('') }] : []),
         ...(dateFrom ? [{ k: 'df', label: 'From ' + fmtDate(dateFrom), remove: () => setDateFrom('') }] : []),
        ...(dateTo ? [{ k: 'dt', label: 'To ' + fmtDate(dateTo), remove: () => setDateTo('') }] : []),
        ...(noEmailOnly ? [{ k: 'ne', label: 'No email', remove: () => setNoEmailOnly(false) }] : []),
         ...(wrongLocOnly ? [{ k: 'wl', label: 'Wrong location', remove: () => setWrongLocOnly(false) }] : []),
        ...(noLocOnly ? [{ k: 'nl', label: 'No location', remove: () => setNoLocOnly(false) }] : []),
        ].map((c) => (
        <span key={c.k} className="inline-flex items-center gap-1.5 text-xs font-medium pl-3 pr-1.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100 capitalize">
          {c.label}
          <button type="button" onClick={c.remove} className="h-4 w-4 rounded-full hover:bg-purple-200 leading-none" aria-label="Remove filter">×</button>
        </span>
      ))}
    </div>
    <p className="text-xs text-gray-400">
      Showing <span className="font-semibold text-purple-600">{filteredEmployers.length}</span> of {employers.length} employers
    </p>
  </div>
</div>

      {/* ---- Add Employer Modal ---- */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeAddForm}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-start justify-between shrink-0">
              <div>
                <h3 className="text-lg font-bold text-white">Add New Employer</h3>
                <p className="text-purple-100 text-xs mt-0.5">Register a company on PlanB Jobs</p>
              </div>
              <button
                onClick={closeAddForm}
                className="text-purple-100 hover:text-white text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto">
              {success && (
                <div className="mb-4 bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg border border-green-100">
                  ✓ Employer created successfully.
                </div>
              )}
              {errors.general && (
                <div className="mb-4 bg-red-50 text-red-700 text-sm px-4 py-2 rounded-lg border border-red-100">
                  {errors.general[0]}
                </div>
              )}

              {/* Section: Contact Details */}
              <div className="mb-5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-purple-600 mb-3 pb-2 border-b border-gray-100">
                  👤 Contact Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {/* Company Name — with AI suggestions */}
                  <div className="mb-4 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company / Contact Name</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleCompanyNameChange}
                      onFocus={() => { if (companySuggestions.length > 0) setShowCompanySuggestions(true); }}
                      onBlur={() => setTimeout(() => setShowCompanySuggestions(false), 150)}
                      autoComplete="off"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D]"
                    />
                    {companyLoading && (
                      <span className="absolute right-3 top-9 text-xs text-gray-400">…</span>
                    )}
                    {showCompanySuggestions && companySuggestions.length > 0 && (
                      <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {companySuggestions.map((s, i) => (
                          <li
                            key={i}
                            onMouseDown={() => pickCompanySuggestion(s)}
                            className="px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600 cursor-pointer"
                          >
                            {s}
                          </li>
                        ))}
                      </ul>
                    )}
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name[0]}</p>}
                  </div>

                  {field('email', 'Email')}
                  {field('phone', 'Phone (10 digit)')}
                  {field('password', 'Set Login Password', 'password')}
                </div>
              </div>

              {/* Section: Location */}
              <div className="mb-5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-purple-600 mb-3 pb-2 border-b border-gray-100">
                  📍 Location
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {/* Current Location — with AI suggestions, replaces City/State/Address */}
                  <div className="mb-4 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Location</label>
                    <input
                      type="text"
                      name="location"
                      value={form.location}
                      onChange={handleLocationChange}
                      onFocus={() => { if (locationSuggestions.length > 0) setShowLocationSuggestions(true); }}
                      onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 150)}
                      autoComplete="off"
                      placeholder="e.g. Bengaluru, Karnataka"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D]"
                    />
                    {locationLoading && (
                      <span className="absolute right-3 top-9 text-xs text-gray-400">…</span>
                    )}
                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                      <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {locationSuggestions.map((s, i) => (
                          <li
                            key={i}
                            onMouseDown={() => pickLocationSuggestion(s)}
                            className="px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600 cursor-pointer"
                          >
                            {s}
                          </li>
                        ))}
                      </ul>
                    )}
                    {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location[0]}</p>}
                  </div>

                  {field('pincode', 'Pincode')}
                </div>
              </div>

              {/* Section: Company Details */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-purple-600 mb-3 pb-2 border-b border-gray-100">
                  🏢 Company Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Website</label>
                    <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-purple-400">
                      <span className="px-3 py-2 text-sm text-gray-400 bg-gray-50 border-r border-gray-200 shrink-0">https://</span>
                      <input
                        type="text"
                        name="company_website"
                        value={form.company_website}
                        onChange={handleWebsiteChange}
                        placeholder="example.com"
                        className="w-full px-3 py-2 text-sm focus:outline-none"
                      />
                    </div>
                    {errors.company_website && <p className="text-red-500 text-xs mt-1">{errors.company_website[0]}</p>}
                  </div>
                  {field('company_gst', 'Company GST')}

                  {/* Company Size — dropdown, same ranges as the registration flow */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Size</label>
                    <select
                      name="company_size"
                      value={form.company_size}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D]"
                    >
                      <option value="">Select company size</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="201-500">201-500</option>
                      <option value="501-1000">501-1000</option>
                      <option value="1000+">1000+</option>
                    </select>
                    {errors.company_size && <p className="text-red-500 text-xs mt-1">{errors.company_size[0]}</p>}
                  </div>

                  {/* Company Industry — with AI suggestions */}
                  <div className="mb-4 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Industry</label>
                    <input
                      type="text"
                      name="company_industry"
                      value={form.company_industry}
                      onChange={handleIndustryChange}
                      onFocus={() => { if (industrySuggestions.length > 0) setShowIndustrySuggestions(true); }}
                      onBlur={() => setTimeout(() => setShowIndustrySuggestions(false), 150)}
                      autoComplete="off"
                      placeholder="e.g. IT Services"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D]"
                    />
                    {industryLoading && (
                      <span className="absolute right-3 top-9 text-xs text-gray-400">…</span>
                    )}
                    {showIndustrySuggestions && industrySuggestions.length > 0 && (
                      <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-100 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                        {industrySuggestions.map((s, i) => (
                          <li
                            key={i}
                            onMouseDown={() => pickIndustrySuggestion(s)}
                            className="px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600 cursor-pointer"
                          >
                            {s}
                          </li>
                        ))}
                      </ul>
                    )}
                    {errors.company_industry && <p className="text-red-500 text-xs mt-1">{errors.company_industry[0]}</p>}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">About Company</label>
                  <textarea
                    name="company_about"
                    value={form.company_about}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
              <button
                type="button"
                onClick={closeAddForm}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white px-6 py-2 rounded-full text-sm font-semibold disabled:opacity-50 shadow-sm"
              >
                {saving ? 'Saving...' : 'Add Employer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {jobsEmployer && (
        <EmployerJobsModal employer={jobsEmployer} onClose={() => setJobsEmployer(null)} />
      )}

      {mergeOpen && selectedEmployers.length === 2 && (
  <MergeEmployersModal
    employers={selectedEmployers}
    loading={mergeLoading}
    onClose={() => setMergeOpen(false)}
    onConfirm={handleMerge}
  />
)}

      {fixOpen && (
  <FixLocationsModal
    values={fixableEmployers.map((e) => e.location || e.city)}
    endpoint="/agent/employers/bulk-city"
    noun="employer"
    onClose={() => setFixOpen(false)}
    onApplied={handleLocationApplied}
  />
)}

      {/* ---- Employer profile popup ---- */}
{viewEmployer && (
  <div
    className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
    onClick={() => setViewEmployer(null)}
  >
    <div
      className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col"
      onClick={(ev) => ev.stopPropagation()}
    >
      <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-10 w-10 rounded-full bg-white/10 text-white text-sm font-semibold flex items-center justify-center shrink-0">
            {(viewEmployer.name || '?').trim().charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-white leading-tight truncate">{viewEmployer.name}</h3>
            <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full bg-white/20 text-white capitalize">
              {viewEmployer.status || 'active'}
            </span>
          </div>
        </div>
        <button
          onClick={() => setViewEmployer(null)}
          className="text-purple-100 hover:text-white hover:bg-white/10 rounded-full h-8 w-8 flex items-center justify-center text-xl leading-none"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <dl className="px-6 py-5 overflow-y-auto text-sm divide-y divide-gray-50">
        {[
          ['Email', viewEmployer.email || '—'],
          ['Phone', viewEmployer.phone || '—'],
                    ['Merged Into', viewEmployer.merged_into_name],
          ['Merged Accounts', viewEmployer.merged_count > 0 ? viewEmployer.merged_count : null],
          ['Merged On', viewEmployer.merged_at ? fmtDate(viewEmployer.merged_at) : null],
          ['Alternate Email', viewEmployer.alternate_email],
          ['Alternate Phone', viewEmployer.alternate_phone],
          ['Location', viewEmployer.location || viewEmployer.city || '—'],
          ['Pincode', viewEmployer.pincode],
          ['Industry', viewEmployer.company_industry],
          ['Company Size', viewEmployer.company_size],
          ['Website', viewEmployer.company_website],
          ['GST', viewEmployer.company_gst],
          ['About', viewEmployer.company_about],
          ['Jobs Posted', viewEmployer.jobs_count ?? 0],
          ['Created', fmtDate(viewEmployer.created_at)],
          ['My Last Contact', viewEmployer.my_last_contact ? fmtDate(viewEmployer.my_last_contact) : '—'],
          ['Assigned To', viewEmployer.is_mine ? 'You' : (viewEmployer.assigned_agent_name || 'Unassigned')],
        ]
          .filter(([, value]) => value !== undefined && value !== null && value !== '')
          .map(([label, value]) => (
            <div key={label} className="flex items-start justify-between gap-4 py-2">
              <dt className="text-gray-400 shrink-0">{label}</dt>
              <dd className="text-gray-700 text-right break-words">{value}</dd>
            </div>
          ))}
      </dl>

      <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0 flex-wrap">
        {viewEmployer.phone ? (
          <a href={`tel:${viewEmployer.phone}`} className={`${CALL_BTN} flex-1 py-2 bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100`}>📞 Call</a>
        ) : (
          <span className={`${CALL_BTN} flex-1 py-2 bg-gray-50 text-gray-300 border-gray-200`}>📞 Call</span>
        )}
        {waLink(viewEmployer.phone) ? (
          <a href={waLink(viewEmployer.phone)} target="_blank" rel="noreferrer" className={`${CALL_BTN} flex-1 py-2 bg-green-50 text-green-600 border-green-200 hover:bg-green-100`}>💬 WhatsApp</a>
        ) : (
          <span className={`${CALL_BTN} flex-1 py-2 bg-gray-50 text-gray-300 border-gray-200`}>💬 WhatsApp</span>
        )}
        <Link
          to={`/agent/employers/${viewEmployer.id}`}
          className="flex-1 text-center text-xs font-semibold py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:opacity-90"
        >
          Full profile
        </Link>
      </div>
    </div>
  </div>
)}

      {selectedIds.size > 0 && (
  <div className="flex items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 mb-3 flex-wrap">
    <span className="text-sm text-purple-700 font-medium">{selectedIds.size} selected</span>
   
   {selectedIds.size === 2 && (
  <button
    onClick={() => setMergeOpen(true)}
    disabled={bulkLoading}
    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:opacity-90 disabled:opacity-50"
  >
    🔗 Merge accounts
  </button>
)}
{selectedIds.size === 1 && (
  <span className="text-xs text-gray-400">Select one more to merge</span>
)}
   
    <button
      onClick={handleBulkDisable}
      disabled={bulkLoading}
      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:opacity-90 disabled:opacity-50"
    >
      {bulkLoading ? 'Disabling…' : 'Disable selected'}
    </button>
    <button
      onClick={() => setSelectedIds(new Set())}
      disabled={bulkLoading}
      className="text-xs font-semibold text-gray-500 hover:text-gray-700"
    >
      Clear
    </button>
  </div>
)}

      <div className="flex items-center justify-between flex-wrap gap-2 mb-2 text-sm text-gray-600">
        <span className="font-semibold">
          Showing {displayedEmployers.length === 0 ? 0 : (curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, displayedEmployers.length)} of {displayedEmployers.length}
        </span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="border border-purple-200 rounded-full px-2 py-1 text-xs bg-white"
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
           <tr className="text-left text-gray-400 text-[10px] uppercase border-b border-gray-100 bg-gray-50/60">
  <th className="px-5 py-3 w-8">
    <input type="checkbox" checked={allSelected()} onChange={toggleSelectAll} />
  </th>
  <SortTh label="Company" k="name" sort={sort} onSort={cycleSort} />
  <SortTh label="Email" k="email" sort={sort} onSort={cycleSort} />
  <SortTh label="Phone" k="phone" sort={sort} onSort={cycleSort} />
  <SortTh label="Location" k="location" sort={sort} onSort={cycleSort} />
  <SortTh label="Jobs" k="jobs" sort={sort} onSort={cycleSort} />
  <SortTh label="Created" k="created" sort={sort} onSort={cycleSort} />
  <SortTh label="My Last Contact" k="lastContact" sort={sort} onSort={cycleSort} />
  {viewScope === 'all' && <SortTh label="Assigned To" k="assigned" sort={sort} onSort={cycleSort} />}
  <th className="px-5 py-3 font-medium sticky right-0 bg-white z-10 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">
    Action
  </th>
</tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="py-6 text-center text-gray-400">Loading…</td></tr>
            )}
            {!loading && loadError && (
              <tr><td colSpan={9} className="py-6 text-center text-red-500">
                Could not load employers. <button onClick={() => loadEmployers(viewScope)} className="underline">Retry</button>
              </td></tr>
            )}
            {!loading && !loadError && filteredEmployers.length === 0 && (
              <tr><td colSpan={9} className="py-6 text-center text-gray-400">
                {hasActiveFilters ? 'No employers match your filters.' : 'No employers yet.'}
              </td></tr>
            )}
            {!loading && !loadError && pagedEmployers.map((e) => (
              <tr key={e.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                <td className="px-5 py-3">
  <input
    type="checkbox"
    checked={selectedIds.has(e.id)}
    disabled={!isSelectable(e)}
    onChange={() => toggleSelect(e.id)}
  />
</td>
                <td className="px-5 py-3">
  <button
    type="button"
    onClick={() => setViewEmployer(e)}
    className="font-medium text-left text-[#7C3AED] hover:underline"
  >
    {isAgentAdded(e) && (
      <span title="Added by agent" className="mr-1 font-bold text-pink-600">(A)</span>
    )}
    {e.name}
    {isMergedAway(e) && (
      <span title={`Merged into ${e.merged_into_name || 'main account'}`} className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">MERGED</span>
    )}
    {hasMerged(e) && (
      <span title={`${e.merged_count} account(s) merged into this one`} className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600">+{e.merged_count}</span>
    )}
  </button>
</td>
                <td className="px-5 py-3">{e.email}</td>
                <td className="px-5 py-3">{e.phone}</td>
                <td className="px-5 py-3">
  {hasWrongLocation(e.location || e.city) ? (
    <span className="text-red-600" title={`Suggested: ${suggestCity(e.location || e.city)}`}>
      {e.location || e.city}
    </span>
  ) : (e.location || e.city || '—')}
</td>
                <td className="px-5 py-3">
                  {Number(e.jobs_count) > 0 ? (
                    <button
                      type="button"
                      onClick={() => setJobsEmployer(e)}
                      className="min-w-[2rem] text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-50 text-purple-600 hover:bg-purple-100"
                    >
                      {e.jobs_count}
                    </button>
                  ) : (
                    <span className="text-gray-400">{e.jobs_count ?? 0}</span>
                  )}
                </td>
                <td className="px-5 py-3">{fmtDate(e.created_at)}</td>
                <td className="px-5 py-3">{e.my_last_contact ? fmtDate(e.my_last_contact) : '—'}</td>
                {viewScope === 'all' && (
                  <td className="px-5 py-3">
                    {e.is_mine ? (
                      <span className="text-xs font-semibold text-[#7C3AED]">You</span>
                    ) : e.assigned_agent_name ? (
                      <span className="text-xs text-gray-500">{e.assigned_agent_name}</span>
                    ) : (
                      <span className="text-xs text-gray-300">Unassigned</span>
                    )}
                  </td>
                )}
<td className="px-5 py-3 sticky right-0 bg-white z-10 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">
  <ActionMenu
    phone={e.phone}
    items={[
      { label: 'View', icon: '👁', tone: 'purple', to: `/agent/employers/${e.id}` },
      isVerified(e)
        ? { label: 'Post Job', icon: '➕', tone: 'blue', to: `/agent/employers/${e.id}/jobs/new` }
        : { label: 'Post Job', icon: '➕', tone: 'off', disabled: true },
      viewScope === 'all' && !e.is_mine && {
        label: assigningId === e.id ? 'Assigning…' : e.assigned_agent_name ? 'Reassign to me' : 'Assign to me',
        icon: '🙋',
        tone: 'green',
        disabled: assigningId === e.id,
        onClick: () => handleAssign(e.id),
      },
      (e.status || 'active') !== 'active'
        ? { label: `Status: ${e.status}`, icon: '⛔', tone: 'off', disabled: true }
        : (viewScope === 'mine' || e.is_mine) && {
            label: 'Disable',
            icon: '🚫',
            tone: 'amber',
            onClick: () => handleDisableEmployer(e),
          },
    ]}
  />
</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(() => {
        const total = displayedEmployers.length;
        const from = total === 0 ? 0 : (curPage - 1) * pageSize + 1;
        const to = Math.min(curPage * pageSize, total);
        const nums = [];
        for (let p = Math.max(1, curPage - 2); p <= Math.min(totalPages, curPage + 2); p++) nums.push(p);
        const btn = 'px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-40';
        return (
          <div className="flex items-center justify-between flex-wrap gap-3 mt-3 text-sm text-gray-600">
            <div className="flex items-center gap-3">
              <span>Showing {from}–{to} of {total}</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border border-purple-200 rounded-full px-2 py-1 text-xs bg-white"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button className={btn} disabled={curPage === 1} onClick={() => setPage(1)}>«</button>
              <button className={btn} disabled={curPage === 1} onClick={() => setPage(curPage - 1)}>Prev</button>
              {nums.map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`${btn} ${p === curPage ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent' : 'bg-white border-purple-200'}`}
                >
                  {p}
                </button>
              ))}
              <button className={btn} disabled={curPage === totalPages} onClick={() => setPage(curPage + 1)}>Next</button>
              <button className={btn} disabled={curPage === totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          </div>
        );
      })()}
    </div>
    </AgentLayout>
  );
}

export default AgentEmployers;